/**
 * SOLID Review
 * S — Strong. One concern: the table's video chat — its policy as the Domain decides it, and the server's signaling and
 *     admin reads around the SDK the Framework drives — as use cases built once. No rule lives here.
 * O — Good. A new media act is one use case built here.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the media ops of `ClientUseCases`.
 * D — Good. Two port interfaces and the shared steps, injected; what it builds is held by role (`IUseCase`).
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type {
  ICommandMediaClock, ICommandMediaIdle, ICommandMediaBanner, ICommandMediaUsed, IPayloadMediaToken, IEntityMediaToken,
  IPayloadMediaConnection, IPayloadMediaHealth, IEntityMediaHealth, IPayloadMediaUsage, IEntityMediaUsage,
} from '../../domain/ports/schemas/MediaMoves';
import type { IPortClientMediaServer } from '../ports/contracts';
import type { TPortOutcome, TPortResult } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { ClientSteps } from '../shared/ClientSteps';
import { DecisionStep } from '../shared/DecisionStep';
import { PassThroughUseCase } from '../shared/PassThroughUseCase';
import { MediaUsedUseCase, type IConfigDepMediaUsed } from './MediaUsedUseCase';

export interface IConfigDepMediaContext {
  readonly decisions: IPortClientDecisions;
  readonly media: IPortClientMediaServer;
  /** The one serial section, shared with every other context. */
  readonly steps: ClientSteps;
}

/**
 * THE TABLE'S VIDEO CHAT (frontend strict-ports plan, FE5b/FE5c, 2026-10-04; use cases since frontend-application-oo P4).
 * useLiveKitSession called the Framework's LiveKit adapter for the token, the connection reports and the presence marks, and
 * kept the opt-in, the idle rule and the banner in React state; the admin reads called the same adapter. The SDK's room,
 * tracks and participants stay the hook's. What is here:
 *
 *   - the POLICY, each one decision in one step: on/off, reconnect, the idle tick (§23.7), the server's removal (§21.10), the
 *     banner — and presence, reported once per kind per room, never for a guest;
 *   - the SIGNALING, passed through with the room and player the SDK connected — which may be a room the table has just
 *     left, so these cannot read the session;
 *   - the ADMIN reads, passed through.
 */
export class MediaContext {
  readonly toggleMedia: IUseCase<ICommandMediaClock, TPortOutcome>;
  readonly reconnectMedia: IUseCase<ICommandMediaClock, TPortOutcome>;
  /** One tick of the idle check — `ClientJobs` runs it while video is on at a table. */
  readonly checkMediaIdle: IUseCase<ICommandMediaIdle, TPortOutcome>;
  readonly mediaRemovedByServer: IUseCase<ICommandNothing, TPortOutcome>;
  readonly dismissMediaBanner: IUseCase<ICommandMediaBanner, TPortOutcome>;
  readonly mediaUsed: IUseCase<ICommandMediaUsed, TPortOutcome>;
  readonly mediaToken: IUseCase<IPayloadMediaToken, TPortResult<IEntityMediaToken>>;
  readonly reportMediaConnection: IUseCase<IPayloadMediaConnection, TPortOutcome>;
  readonly checkMediaHealth: IUseCase<IPayloadMediaHealth, TPortResult<IEntityMediaHealth>>;
  readonly loadMediaUsageReport: IUseCase<IPayloadMediaUsage, TPortResult<IEntityMediaUsage>>;

  constructor(deps: IConfigDepMediaContext) {
    const { decisions: d, media, steps } = deps;
    const step = steps.onSession;
    this.toggleMedia = new DecisionStep<IPropsClientInput, ICommandMediaClock>(step, (ctx, input, command) => d.mediaToggled(ctx, input, command));
    this.reconnectMedia = new DecisionStep<IPropsClientInput, ICommandMediaClock>(step, (ctx, input, command) => d.mediaReconnect(ctx, input, command));
    this.checkMediaIdle = new DecisionStep<IPropsClientInput, ICommandMediaIdle>(step, (ctx, input, command) => d.mediaIdleChecked(ctx, input, command));
    this.mediaRemovedByServer = new DecisionStep<IPropsClientInput, ICommandNothing>(step, (ctx, input, command) => d.mediaRemovedByServer(ctx, input, command));
    this.dismissMediaBanner = new DecisionStep<IPropsClientInput, ICommandMediaBanner>(step, (ctx, input, command) => d.mediaBannerDismissed(ctx, input, command));
    const presence: IConfigDepMediaUsed = { decisions: d, media, step };
    this.mediaUsed = new MediaUsedUseCase(presence);
    this.mediaToken = new PassThroughUseCase<IPayloadMediaToken, TPortResult<IEntityMediaToken>>((ctx, command) => media.token(ctx, command));
    this.reportMediaConnection = new PassThroughUseCase<IPayloadMediaConnection, TPortOutcome>((ctx, command) => media.reportConnection(ctx, command));
    this.checkMediaHealth = new PassThroughUseCase<IPayloadMediaHealth, TPortResult<IEntityMediaHealth>>((ctx, command) => media.health(ctx, command));
    this.loadMediaUsageReport = new PassThroughUseCase<IPayloadMediaUsage, TPortResult<IEntityMediaUsage>>((ctx, command) => media.usageReport(ctx, command));
  }
}
